/**
 * @fileoverview Cookie Settings Manager JS
 * Handles interactions with OneTrust cookie consent management
 */

/**
 * Custom element that provides a button to open OneTrust cookie preferences
 * @class
 * @extends HTMLElement
 */
class CookieSettingsButton extends HTMLElement {
  /**
   * Creates an instance of CookieSettingsButton
   * Initializes properties and binds event listeners
   */
  constructor() {
    super();
    this.fallbackMessage = this.getAttribute('fallback-message');
  }
  
  connectedCallback() {
    this.bindEvents();
  }

  /**
   * Binds event listeners for button click and OneTrust availability
   */
  bindEvents() {
    this.addEventListener('click', this.openCookiePreferences.bind(this));
    document.addEventListener('DOMContentLoaded', this.checkOneTrustAvailability.bind(this));
  }

  /**
   * Gets the appropriate OneTrust instance (supports multiple versions)
   * @returns {Object|null} The OneTrust API object or null if not available
   */
  getOneTrustAPI() {
    if (window.OneTrust?.ToggleInfoDisplay) {
      return window.OneTrust;
    } else if (window.Optanon?.ToggleInfoDisplay) {
      return window.Optanon;
    }
    return null;
  }

  /**
   * Opens the OneTrust cookie preferences UI
   * Falls back to Optanon for older versions
   * Shows fallback message if neither is available
   */
  openCookiePreferences() {
    const oneTrustAPI = this.getOneTrustAPI();
    
    if (oneTrustAPI) {
      oneTrustAPI.ToggleInfoDisplay();
      this.listenForModalClose();
    } else {
      alert(this.fallbackMessage || 'Cookie settings are not available at this time. Please clear your browser cookies and reload the page.');
    }
  }

  /**
   * Creates a mutation observer to detect when the OneTrust modal is closed
   */
  listenForModalClose() {
    const pcSdk = document.getElementById('onetrust-pc-sdk');
    if (!pcSdk) return;
    
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'style' || mutation.attributeName === 'class') {
          if (window.getComputedStyle(pcSdk).display === 'none' || pcSdk.classList.contains('ot-hide')) {
            observer.disconnect();
            break;
          }
        }
      }
    });
    
    observer.observe(pcSdk, { attributes: true });
  }

  /**
   * Checks if OneTrust is available and adds onetrust-available class if it is
   * Polls for 10 seconds before giving up
   */
  checkOneTrustAvailability() {
    let attempts = 0;
    const MAX_ATTEMPTS = 20; // 10 seconds at 500ms intervals
    
    const checkOneTrust = setInterval(() => {
      if (this.getOneTrustAPI()) {
        clearInterval(checkOneTrust);
        this.classList.add('onetrust-available');
      } else if (++attempts >= MAX_ATTEMPTS) {
        clearInterval(checkOneTrust);
      }
    }, 500);
  }
}

/**
 * Checks if OneTrust consent has been given
 * @returns {boolean} True if consent has been given
 */
function hasOneTrustConsent() {
  return document.cookie.split(';').some(c => c.trim().startsWith('OptanonAlertBoxClosed='));
}

/**
 * Initializes consent detection and adds appropriate classes to the body element
 */
function initConsentDetection() {
  if (hasOneTrustConsent()) {
    document.body.classList.add('consent-given');
  }

  window.addEventListener('OneTrustGroupsUpdated', () => {
    document.body.classList.add('consent-given');
  });
}

// Initialize on DOM content loaded
document.addEventListener('DOMContentLoaded', initConsentDetection);

// Register the custom element
customElements.define('cookie-settings-button', CookieSettingsButton);